import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// Capability-based rather than width-based: a narrow desktop window still has a mouse,
// and a touch laptop should not get the mouse path.
const POINTER_QUERY = "(hover: hover) and (pointer: fine)";

// The touch heuristic: a reader who got deep enough to be engaged (latching — a later
// flick counts even if it starts shallower) and then flicks back into the top band is
// heading for the browser chrome. The landing zone does the real work; momentum scrolling
// samples 2-6 px/ms per frame, so velocity alone can't tell leaving from scrolling back
// to re-read.
const MIN_ENGAGED_DEPTH_PX = 300;
const EXIT_LANDING_ZONE_PX = 150;
const EXIT_VELOCITY_PX_PER_MS = 1.5; // upward

function isExitFlick(previousY: number, currentY: number, elapsedMs: number) {
  if (elapsedMs <= 0) return false; // guards against Infinity
  if (currentY > EXIT_LANDING_ZONE_PX) return false;
  return (previousY - currentY) / elapsedMs > EXIT_VELOCITY_PX_PER_MS;
}

/**
 * Fires `onExit` once, the first time the visitor signals they're leaving: the pointer
 * exiting through the top of the document on mouse devices, or an engaged reader flicking
 * back to the top on touch devices. No-op while `enabled` is false.
 */
export function useExitIntent(onExit, enabled) {
  const $ = _c(5);
  const hasFired = useRef(false);
  const hasEngagedRef = useRef(false);
  const lastSample = useRef(null);
  const isMouseDevice = useMediaQuery(POINTER_QUERY);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== isMouseDevice || $[2] !== onExit) {
    t0 = () => {
      if (!enabled || hasFired.current) {
        return;
      }
      const fire = () => {
        if (hasFired.current) {
          return;
        }
        hasFired.current = true;
        onExit();
      };
      if (isMouseDevice) {
        const onMouseOut = event => {
          if (event.relatedTarget !== null) {
            return;
          }
          if (event.clientY > 0) {
            return;
          }
          fire();
        };
        document.addEventListener("mouseout", onMouseOut);
        return () => document.removeEventListener("mouseout", onMouseOut);
      }
      const onScroll = () => {
        const now = Date.now();
        const currentY = window.scrollY;
        const previous = lastSample.current;
        lastSample.current = {
          y: currentY,
          t: now
        };
        if (currentY > MIN_ENGAGED_DEPTH_PX) {
          hasEngagedRef.current = true;
        }
        if (!previous || !hasEngagedRef.current) {
          return;
        }
        if (isExitFlick(previous.y, currentY, now - previous.t)) {
          fire();
        }
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", onScroll);
    };
    t1 = [onExit, enabled, isMouseDevice];
    $[0] = enabled;
    $[1] = isMouseDevice;
    $[2] = onExit;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}